import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'

import { admin } from './admin.ts'
import type {
  AgentDirectoryPage,
  AgentDirectoryQuery,
  DetectionQuery,
  LogHistoryQuery,
  LogPage,
  ProcessTimelineQuery,
} from './admin.ts'
import { adminAiFindings, tenantAiFindings } from './aiFindings.ts'
import type { AiDecision, AiFinding, AiFindingDetail, AiFindingPage, AiFindingQuery, EventAiFindings } from './aiFindings.ts'
import { TENANT_LOG_STREAM_URL, tenant } from './tenant.ts'
import { LOG_STREAM_URL, logStreamUrl } from '../sse/useLogStream.ts'
import type {
  DetectionPage,
  LogDetail,
  LogFilter,
  LogRow,
  ProcessTimelinePage,
  ProcessTree,
} from '../sse/types.ts'

/**
 * A "fonte" da telemetria: de onde as telas de Detecções, Logs e Processo leem.
 *
 * As telas são as mesmas nas duas experiências; muda a superfície. A Black Ghost
 * lê `/api/admin` (com `tenantId` como filtro opcional); o cliente lê
 * `/api/tenant`, onde a empresa vem do token. Injetar a fonte, em vez de duplicar
 * as telas, mantém uma leitura só do mesmo evento nas duas pontas.
 *
 * A fonte de cliente DESCARTA `tenantId` de qualquer query que receba: as funções
 * de `tenant.ts` montam a query campo a campo e não conhecem a chave. A segurança
 * é do backend; isto só garante que o Hermes nunca peça o que não deveria.
 */
export interface TelemetrySource {
  kind: 'admin' | 'tenant'
  /**
   * A empresa está sempre no escopo da consulta. Na rota de cliente o servidor a
   * põe pelo token (§6.3), então os filtros que "exigem empresa" valem sem seletor.
   */
  alwaysScoped: boolean
  /** Seletor de empresa, ações de comando e colunas de empresa só fazem sentido no console. */
  multiTenant: boolean
  logs: (params: LogHistoryQuery, signal?: AbortSignal) => Promise<LogPage>
  logDetail: (row: LogRow, signal?: AbortSignal) => Promise<LogDetail>
  detections: (params: DetectionQuery, signal?: AbortSignal) => Promise<DetectionPage>
  processTree: (entityId: string, signal?: AbortSignal) => Promise<ProcessTree>
  processTimeline: (
    entityId: string,
    params: ProcessTimelineQuery,
    signal?: AbortSignal,
  ) => Promise<ProcessTimelinePage>
  agents: (params: AgentDirectoryQuery, signal?: AbortSignal) => Promise<AgentDirectoryPage>
  /** URL do tail SSE; a identidade do stream (§0.4). */
  streamUrl: (filter: LogFilter) => string
  aiFindings: (params: AiFindingQuery, signal?: AbortSignal) => Promise<AiFindingPage>
  aiFinding: (id: string, signal?: AbortSignal) => Promise<AiFindingDetail>
  logAiFindings: (logId: number, signal?: AbortSignal) => Promise<EventAiFindings>
  /** Decisão do analista. Só no console: o cliente não tem a rota, e não ganha os botões. */
  decideAiFinding: ((id: string, decision: AiDecision) => Promise<AiFinding>) | null
}

export const ADMIN_SOURCE: TelemetrySource = {
  kind: 'admin',
  alwaysScoped: false,
  multiTenant: true,
  logs: admin.logs,
  logDetail: admin.logDetail,
  detections: admin.detections,
  processTree: admin.processTree,
  processTimeline: admin.processTimeline,
  agents: admin.agents,
  streamUrl: (filter) => logStreamUrl(LOG_STREAM_URL, filter, true),
  aiFindings: adminAiFindings.list,
  aiFinding: adminAiFindings.detail,
  logAiFindings: adminAiFindings.forLog,
  decideAiFinding: adminAiFindings.decide,
}

export const TENANT_SOURCE: TelemetrySource = {
  kind: 'tenant',
  alwaysScoped: true,
  multiTenant: false,
  logs: (params, signal) => tenant.logs(params, signal),
  logDetail: tenant.logDetail,
  detections: (params, signal) => tenant.detections(params, signal),
  processTree: tenant.processTree,
  processTimeline: tenant.processTimeline,
  agents: (params, signal) =>
    tenant.agents({ page: params.page, pageSize: params.pageSize, search: params.search }, signal),
  streamUrl: (filter) => logStreamUrl(TENANT_LOG_STREAM_URL, filter, false),
  aiFindings: (params, signal) => tenantAiFindings.list({ ...params, tenantId: undefined }, signal),
  aiFinding: tenantAiFindings.detail,
  logAiFindings: tenantAiFindings.forLog,
  decideAiFinding: null,
}

/** Default = console: as telas da Black Ghost não precisam de provider. */
const SourceContext = createContext<TelemetrySource>(ADMIN_SOURCE)

export function TelemetrySourceProvider({
  source,
  children,
}: {
  source: TelemetrySource
  children: ReactNode
}) {
  return <SourceContext.Provider value={source}>{children}</SourceContext.Provider>
}

export function useTelemetrySource(): TelemetrySource {
  return useContext(SourceContext)
}
